import { useAuth } from "../../context";
import { useMutation, useQuery } from "react-query";
import { ReportsApi } from "../../api";

export const reportKeys = {
  reportData: () => ["reportData"],
  suppliersReportData: () => ["suppliersReportData"],
  aggregatedSuppliersData: () => ["aggregatedSuppliersData"],
  dimensionReport: () => ["dimensionReport"],
  estimatedPurchasedGoodsAndServices: () => ["estimatedPurchasedGoodsAndServices"],
  capitalGoods: () => ["capitalGoods"],
  upstreamTransportationAndDistribution: () => ["upstreamTransportationAndDistribution"],
  wasteGeneratedInOperations: () => ["wasteGeneratedInOperations"],
  downstreamTransportationAndDistribution: () => ["downstreamTransportationAndDistribution"],
  treatmentOfSoldProducts: () => ["treatmentOfSoldProducts"],
  companyTotalEmissions: () => ["companyTotalEmissions"],
  companyDimensionEmissions: () => ["companyDimensionEmissions"],
  questionnairesNotes: () => ["questionnairesNotes"],
  lastThreeYearsEmissions: () => ["lastThreeYearsEmissions"],
  lastThreeYearsEmissionsItemsCount: () => ["lastThreeYearsEmissionsItemsCount"],
  supplierEngagementReports: () => ["supplierEngagementReports"],
  numberOfSupplierQuestionnairesByStatus: () => ["numberOfSupplierQuestionnairesByStatus"],
  numberOfFinalisedSupplierQuestionnairesByType: () => ["numberOfFinalisedSupplierQuestionnairesByType"],
  supplierEmissionsReport: () => ["supplierEmissionsReport"],
  supplierEmissionsPerYear: () => ["supplierEmissionsPerYear"],
  suppliersSpends: () => ["suppliersSpends"],
  suppliersTotalSpends: () => ["suppliersTotalSpends"],
  topFiveInactiveSuppliers: () => ["topFiveInactiveSuppliers"],
  suppliersSpendsByLocation: () => ["suppliersSpendsByLocation"],
  reportId: () => ["reportId"]
};

export function useGetReport(params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.reportData(), params.year, params.quarter],
    () => callApi(ReportsApi.getCalculatedReport, params), config);
}

export function useExportReport(config) {
  const { callApi } = useAuth();
  return useMutation(({ params }) => callApi(ReportsApi.getExportReport, params), config);
}

export function useUpdateTurnover(config) {
  const { callApi } = useAuth();
  return useMutation(({reportId, turnover}) => callApi(ReportsApi.updateTurnover, { reportId, turnover }), config);
}

export function useGetSuppliersDataReport(params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.suppliersReportData(), params.year, params.quarter],
    () => callApi(ReportsApi.getSuppliersDataReport, params), config);
}

export function useGetDimensionsReport(reportId, dimensionItemIds, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.dimensionReport(), reportId, dimensionItemIds],
    () => callApi(ReportsApi.getDimensionsReport, { reportId, dimensionItemIds }), config);
}

export function useGetAggregatedSuppliersDataReport(params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.aggregatedSuppliersData(), params.year, params.quarter],
    () => callApi(ReportsApi.getAggregatedSuppliersDataReport, params), config);
}

export function useExportScopeOneAndTwoTotalData(config) {
  const { callApi } = useAuth();
  return useMutation(({ params }) => callApi(ReportsApi.exportScopeOneAndTwoTotalData, params), config);
}

export function useExportScopeOneAndTwoDimensionsData(reportId, dimensionItemIds, config) {
  const { callApi } = useAuth();
  return useMutation(() => callApi(ReportsApi.exportScopeOneAndTwoDimensionsData, { reportId, dimensionItemIds }), config);
}

export function useExportSuppliersAggregatedData(config) {
  const { callApi } = useAuth();
  return useMutation(({ params }) => callApi(ReportsApi.exportSuppliersAggregatedData, params), config);
}

export function useGetEstimatedPurchasedGoodsAndServices(reportId, params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.estimatedPurchasedGoodsAndServices(), reportId, params.page, params.size],
    () => callApi(ReportsApi.getEstimatedPurchasedGoodsAndServices, { reportId, params }), config);
}

export function useGetCapitalGoods(reportId, params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.capitalGoods(), reportId, params.page, params.size],
    () => callApi(ReportsApi.getCapitalGoods, { reportId, params }), config);
}

export function useGetUpstreamTransportationAndDistribution(reportId, params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.upstreamTransportationAndDistribution(), reportId, params.page, params.size],
    () => callApi(ReportsApi.getUpstreamTransportationAndDistribution, { reportId, params }), config);
}

export function useGetWasteGeneratedInOperations(reportId, params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.wasteGeneratedInOperations(), reportId, params.page, params.size],
    () => callApi(ReportsApi.getWasteGeneratedInOperations, { reportId, params }), config);
}

export function useGetDownstreamTransportationAndDistribution(reportId, params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.downstreamTransportationAndDistribution(), reportId, params.page, params.size],
    () => callApi(ReportsApi.getDownstreamTransportationAndDistribution, { reportId, params }), config);
}

export function useGetTreatmentOfSoldProducts(reportId, params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.treatmentOfSoldProducts(), reportId, params.page, params.size],
    () => callApi(ReportsApi.getTreatmentOfSoldProducts, { reportId, params }), config);
}

export function useGetCompanyTotalEmissionsReports(params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.companyTotalEmissions(), params.year, params.quarter],
    () => callApi(ReportsApi.getCompanyTotalEmissionsReports, params), config);
}

export function useGetCompanyDimensionReports(dimensionId, params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.companyDimensionEmissions(), dimensionId, params.year, params.quarter, params.page, params.size],
    () => callApi(ReportsApi.getCompanyDimensionReports, { dimensionId, params }), config);
}

export function useGetQuestionnairesNotes(params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.questionnairesNotes(), params.year, params.quarter],
    () => callApi(ReportsApi.getQuestionnairesNotes, params), config);
}

export function useGetLastThreeYearsEmissions(dimensionId, params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.lastThreeYearsEmissions(), dimensionId, params.page, params.size],
    () => callApi(ReportsApi.getLastThreeYearsEmissions, { dimensionId, params }), config);
}

export function useGetLastThreeYearsEmissionsItemsCount(dimensionId, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.lastThreeYearsEmissionsItemsCount(), dimensionId],
    () => callApi(ReportsApi.getLastThreeYearsEmissionsItemsCount, dimensionId), config);
}

export function useGetSupplierEngagementReports(categoryId, supplierIds, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.supplierEngagementReports(), categoryId, supplierIds],
    () => callApi(ReportsApi.getSupplierEngagementReports, {categoryId, supplierIds}), config);
}

export function useGetNumberOfSupplierQuestionnairesByStatus(categoryId, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.numberOfSupplierQuestionnairesByStatus(), categoryId],
    () => callApi(ReportsApi.getNumberOfSupplierQuestionnairesByStatus, categoryId), config);
}

export function useGetNumberOfFinalisedSupplierQuestionnairesByType(categoryId, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.numberOfFinalisedSupplierQuestionnairesByType(), categoryId],
    () => callApi(ReportsApi.getNumberOfFinalisedSupplierQuestionnairesByType, categoryId), config);
}

export function useGetSupplierEmissionsReport(supplierId, params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.supplierEmissionsReport(), supplierId, params.year, params.quarter],
    () => callApi(ReportsApi.getSupplierEmissionsReport, { supplierId, params }), config);
}

export function useGetSupplierEmissionsPerYear(supplierId, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.supplierEmissionsPerYear(), supplierId],
    () => callApi(ReportsApi.getSupplierEmissionsPerYear, supplierId), config);
}

export function useGetSuppliersSpends(categoryId, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.suppliersSpends(), categoryId],
    () => callApi(ReportsApi.getSuppliersSpends, categoryId), config);
}

export function useGetSuppliersTotalSpends(categoryId, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.suppliersTotalSpends(), categoryId],
    () => callApi(ReportsApi.getSuppliersTotalSpends, categoryId), config);
}

export function useGetTopFiveInactiveSuppliers(categoryId, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.topFiveInactiveSuppliers(), categoryId],
    () => callApi(ReportsApi.getTopFiveInactiveSuppliers, categoryId), config);
}

export function useGetSuppliersSpendsByLocation({ year, categoryId, supplierId }, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.suppliersSpendsByLocation(), year, categoryId, supplierId],
    () => callApi(ReportsApi.getSuppliersSpendsByLocation, { year, categoryId, supplierId }), config);
}

export function useExportConsumptionsData(config) {
  const { callApi } = useAuth();
  return useMutation(({ dimensionId, queryParams }) =>
    callApi(ReportsApi.exportConsumptionsData, { dimensionId, queryParams }), config);
}

export function useGetReportId(params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.reportId(), params.year, params.quarter],
    () => callApi(ReportsApi.getReportId, params), config);
}